import * as z from "zod/mini";

const canonical_ulid_re = /^[0-9A-HJKMNP-TV-Z]{26}$/;
const crockford_base32_alphabet = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
const ulid_sort_offset = 1n << 128n;
const local_message_sort_offset = 1n << 129n;

export const message_id_schema = z.union([
    z.int().check(z.nonnegative()),
    z.string().check(z.regex(canonical_ulid_re)),
]);

export type MessageId = z.infer<typeof message_id_schema> | number;

export function is_local_message_id(id: MessageId): id is number {
    return typeof id === "number" && !Number.isInteger(id);
}

export function parse_message_id(raw: string): MessageId {
    if (/^\d+$/.test(raw)) {
        return Number.parseInt(raw, 10);
    }

    if (/^\d+\.\d+$/.test(raw)) {
        return Number.parseFloat(raw);
    }

    return message_id_schema.parse(raw.toUpperCase());
}

export function compare_message_ids(a: MessageId, b: MessageId): number {
    if (a === b) {
        return 0;
    }

    if (typeof a === "number" && typeof b === "number") {
        if (is_local_message_id(a) !== is_local_message_id(b)) {
            return is_local_message_id(a) ? 1 : -1;
        }
        return a - b;
    }

    if (typeof a === "string" && typeof b === "string") {
        return a.localeCompare(b);
    }

    if (typeof a === "string") {
        return is_local_message_id(b) ? -1 : 1;
    }

    return is_local_message_id(a) ? 1 : -1;
}

function ulid_to_bigint(id: string): bigint {
    let value = 0n;
    for (const character of id) {
        value =
            value * 32n +
            BigInt(crockford_base32_alphabet.indexOf(character.toUpperCase()));
    }
    return value;
}

function sortable_message_id_value(id: MessageId): bigint {
    if (typeof id === "string") {
        return ulid_sort_offset + ulid_to_bigint(id);
    }

    if (is_local_message_id(id)) {
        return local_message_sort_offset + BigInt(Math.round(id * 1_000_000));
    }

    return BigInt(id);
}

export function is_better_closest_message_id(
    target: MessageId,
    current: MessageId,
    candidate: MessageId,
): boolean {
    const target_value = sortable_message_id_value(target);
    const current_distance = sortable_message_id_value(current) - target_value;
    const candidate_distance = sortable_message_id_value(candidate) - target_value;
    const current_absolute_distance =
        current_distance < 0n ? -current_distance : current_distance;
    const candidate_absolute_distance =
        candidate_distance < 0n ? -candidate_distance : candidate_distance;

    if (candidate_absolute_distance !== current_absolute_distance) {
        return candidate_absolute_distance < current_absolute_distance;
    }

    return compare_message_ids(candidate, current) < 0;
}

export function max_message_id(a: MessageId, b: MessageId): MessageId {
    return compare_message_ids(a, b) >= 0 ? a : b;
}

export function min_message_id(a: MessageId, b: MessageId): MessageId {
    return compare_message_ids(a, b) <= 0 ? a : b;
}

export function get_max_message_id(ids: Iterable<MessageId>): MessageId | undefined {
    let current_max: MessageId | undefined;

    for (const id of ids) {
        current_max = current_max === undefined ? id : max_message_id(current_max, id);
    }

    return current_max;
}

export function get_min_message_id(ids: Iterable<MessageId>): MessageId | undefined {
    let current_min: MessageId | undefined;

    for (const id of ids) {
        current_min = current_min === undefined ? id : min_message_id(current_min, id);
    }

    return current_min;
}

export function sorted_message_ids(ids: Iterable<MessageId>): MessageId[] {
    const id_list = [...new Set(ids)];
    id_list.sort(compare_message_ids);
    return id_list;
}
